import { useCallback } from 'react';
import {
  ANALYTICS_EVENTS,
  type ActionArchivedProps,
  type ActionExpandedProps,
  type ActionMarkedCompletedProps,
  type ActionOpenedInGongProps,
  type ActionViewLoadedProps,
  type ActionViewTabChangedProps,
  type AnalyticsEventName,
  type IdentityProps,
} from '@shared/analytics/events';
import { signalActionRecordId } from '@shared/schemas/terra-signals';
import { useSegmentTrack } from '../../../hooks/useSegmentTrack';

/**
 * The one client-event emitter for the Signals tab (restores the six Action
 * View events — plan 2026-10-23-001 U5). Wraps `useSegmentTrack` (identity
 * spine + legacy aliases) and adds the `source: 'signals'` marker. Callers
 * pass the row's `signalId`; it is replaced with `signalActionId`, the raw
 * Salesforce `Signal_Action__c` id the old Action View events carried (null
 * for non-`signal_action:` ids).
 *
 * Typed per event from the catalog schemas (`src/shared/analytics/events.ts`):
 * the prop shape for each event is its schema's inferred type minus what this
 * wrapper owns (the identity spine, `source`, and `signalActionId`, which
 * `signalId` stands in for) — so a misspelled or missing prop fails typecheck.
 */
type SignalsEventProps = {
  [ANALYTICS_EVENTS.ACTION_MARKED_COMPLETED]: ActionMarkedCompletedProps;
  [ANALYTICS_EVENTS.ACTION_ARCHIVED]: ActionArchivedProps;
  [ANALYTICS_EVENTS.ACTION_EXPANDED]: ActionExpandedProps;
  [ANALYTICS_EVENTS.ACTION_VIEW_LOADED]: ActionViewLoadedProps;
  [ANALYTICS_EVENTS.ACTION_VIEW_TAB_CHANGED]: ActionViewTabChangedProps;
  [ANALYTICS_EVENTS.ACTION_OPENED_IN_GONG]: ActionOpenedInGongProps;
}

/** The six Signals-tab events, checked against the catalog's event-name union. */
export type SignalsEventName = Extract<AnalyticsEventName, keyof SignalsEventProps>;

type WithSignalId<P> = 'signalActionId' extends keyof P ? { signalId: string } : unknown;

export type SignalsClientProps<E extends SignalsEventName> = Omit<
  SignalsEventProps[E],
  keyof IdentityProps | 'source' | 'signalActionId'
> &
  WithSignalId<SignalsEventProps[E]>;

export function useTrackSignalsEvent(): <E extends SignalsEventName>(event: E, props: SignalsClientProps<E>) => void {
  const track = useSegmentTrack();
  return useCallback(
    <E extends SignalsEventName>(event: E, props: SignalsClientProps<E>) => {
      const { signalId, ...rest } = props as SignalsClientProps<E> & { signalId?: string };
      track(event, {
        ...rest,
        ...(signalId !== undefined ? { signalActionId: signalActionRecordId(signalId) } : {}),
        source: 'signals',
      });
    },
    [track],
  );
}
